import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import { api, eur, errMsg } from "@/lib/api";
import { ContactFields, EMPTY_CUSTOMER, contactValid, consentMissing } from "@/components/shop/ContactFields";
import { useOptionDefs } from "@/lib/options";
import { ExVat, Totals, SHIPPING } from "@/components/shop/Totals";

export function InquiryDialog({ product, options, total, onClose, onDone }) {
  const { t, lang } = useLang();
  const [defs] = useOptionDefs();
  const optName = (k) => (lang !== "lt" ? defs[k]?.name_en : defs[k]?.name_lt) || t(`opt_${k}`);
  const [c, setC] = useState(EMPTY_CUSTOMER);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!contactValid(c)) return toast.error(t("fill_all"));
    if (consentMissing(c, t, toast)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inquiries", { type: "options", product_id: product.id, options: options.map((o) => o.key), customer: c });
      onDone(data);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inquiry-dialog" className="bg-[#292D3C] border-[#3B4157] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase text-2xl">{t("send_inquiry")}</DialogTitle></DialogHeader>
        <div className="border border-[#3B4157] p-4 text-sm space-y-1">
          <div className="flex justify-between"><span>{product.name}{product.sku && <span className="text-slate-500 font-mono-x text-xs ml-2">{product.sku}</span>}</span>
            {product.price_on_request ? <span data-testid="inquiry-ask-price" className="text-orange-500 font-semibold">{t("ask_price")}</span> : <span className="font-mono-x">{eur(product.price)}<ExVat /></span>}</div>
          {product.fits && <div className="text-xs text-slate-400">{t("fits_label")}: {product.fits}</div>}
          {options.map((o) => <div key={o.key} className="flex justify-between text-slate-400"><span>+ {optName(o.key)}</span></div>)}
          {!product.price_on_request && !options.length && <div className="pt-2 border-t border-[#3B4157] mt-2"><Totals net={total} shipping={SHIPPING} testid="inquiry-total" big="text-2xl" /></div>}
        </div>
        <p className="text-xs text-amber-400">{product.price_on_request ? t("ask_price_note") : options.length ? t("offer_note") : t("inquiry_note")}</p>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <ContactFields c={c} setC={setC} prefix="inquiry" />
          <button data-testid="inquiry-submit-btn" disabled={busy} className="sm:col-span-2 h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("send_inquiry")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
